import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertOctagon, Plus, Search, Filter, FileClock, Ban, Users as UsersIcon, X, Pencil, Download, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/constants";
import { COMMON_STATUS_LABEL, SEVERITY_LABEL, ACTION_LABEL } from "@/lib/i18nLabels";
import StickyContextBar from "@/components/StickyContextBar";
import IssueDrawer from "@/components/critical/IssueDrawer";

const STATUS_OPTS = [
  { v: "open", label: COMMON_STATUS_LABEL.open, cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { v: "in_review", label: COMMON_STATUS_LABEL.in_review, cls: "bg-sky-100 text-sky-800 border-sky-300" },
  { v: "decision_required", label: COMMON_STATUS_LABEL.decision_required, cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { v: "in_progress", label: COMMON_STATUS_LABEL.in_progress, cls: "bg-orange-100 text-orange-800 border-orange-300" },
  { v: "resolved", label: COMMON_STATUS_LABEL.resolved, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { v: "closed", label: COMMON_STATUS_LABEL.closed, cls: "bg-slate-200 text-slate-500 border-slate-400" },
];
const SEV_OPTS = [
  { v: "low", label: SEVERITY_LABEL.low, cls: "bg-slate-100 text-slate-600 border-slate-300" },
  { v: "medium", label: SEVERITY_LABEL.medium, cls: "bg-amber-50 text-amber-700 border-amber-300" },
  { v: "high", label: SEVERITY_LABEL.high, cls: "bg-orange-50 text-orange-700 border-orange-300" },
  { v: "critical", label: SEVERITY_LABEL.critical, cls: "bg-rose-50 text-rose-700 border-rose-400 font-bold" },
];

/**
 * CriticalIssuesPage — 149-bv. Modul persoalan manajemen dgn Decision Log terintegrasi.
 * BUKAN pengganti Tasks. Referensi ke integrity finding/project/asset via ID (no dupe).
 */
export default function CriticalIssuesPage() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  // 149-cn (PROMPT 16-P0): top-level route guard. Hapus hardcoded BOD Lv1 bypass —
  // canPerm sudah otomatis return true untuk BOD Lv1 (super-admin failsafe).
  const canView = canPerm(user, "critical_issues", "view");
  const canCreate = canPerm(user, "critical_issues", "create");
  const canUpdate = canPerm(user, "critical_issues", "update");
  const canResolveClose = canPerm(user, "critical_issues", "delete");
  const canRecordDecision = canPerm(user, "critical_issue_decisions", "create");
  // 149-cq (PROMPT 16-R2A): Kelola Kategori — gated critical_issue_categories.update.
  const canManageCategories = canPerm(user, "critical_issue_categories", "update");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ status: "all", severity: "all", source: "all", q: "" });
  const [selectedId, setSelectedId] = useState(null);
  // 149-ce: drawer state supports create + edit dgn record existing.
  const [drawer, setDrawer] = useState({ open: false, existing: null });
  const [decisionDlg, setDecisionDlg] = useState({ open: false, issue: null });
  const [categoryDlg, setCategoryDlg] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.severity !== "all") params.set("severity", filters.severity);
      if (filters.source !== "all") params.set("source", filters.source);
      if (filters.q.trim()) params.set("q", filters.q.trim());
      const url = `/critical-issues/export${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = `critical_issues_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Export siap");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal export");
    } finally { setExporting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== "all") params.status = filters.status;
      if (filters.severity !== "all") params.severity = filters.severity;
      if (filters.source !== "all") params.source = filters.source;
      // 149-bw: pakai /scoped supaya server return hanya issue yg visible ke user (scope-aware).
      // 149-ce: parallel load projects untuk resolve project_id → name di detail.
      const [{ data }, cats, prjs] = await Promise.all([
        api.get("/critical-issues/scoped", { params }),
        api.get("/critical-issues/categories/all").catch(() => ({ data: [] })),
        api.get("/active-projects").catch(() => ({ data: [] })),
      ]);
      setItems(data || []);
      setCategories(cats.data || []);
      setProjects(prjs.data || []);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat critical issues");
    } finally { setLoading(false); }
  }, [filters.status, filters.severity, filters.source]);
  // 149-cn (PROMPT 16-P0): jangan fetch data kalau tidak berhak view.
  useEffect(() => { if (canView) load(); }, [canView, load]);

  const filtered = useMemo(() => {
    if (!filters.q.trim()) return items;
    const q = filters.q.toLowerCase();
    return items.filter((i) => (i.title || "").toLowerCase().includes(q) || (i.code || "").toLowerCase().includes(q));
  }, [items, filters.q]);

  useEffect(() => { if (filtered.length && !filtered.find((x) => x.id === selectedId)) setSelectedId(filtered[0].id); }, [filtered, selectedId]);
  const selected = filtered.find((i) => i.id === selectedId);

  const kpi = useMemo(() => {
    let overdue = 0, noPic = 0, awaitingDec = 0, fromIntegrity = 0, resolved = 0;
    const today = new Date().toISOString().slice(0, 10);
    for (const i of items) {
      if (i.target_resolve_date && i.target_resolve_date < today && !["resolved", "closed"].includes(i.status)) overdue += 1;
      if (!i.pic_id) noPic += 1;
      if (i.status === "decision_required") awaitingDec += 1;
      if (i.source === "data_integrity") fromIntegrity += 1;
      if (i.status === "resolved") resolved += 1;
    }
    return { overdue, noPic, awaitingDec, fromIntegrity, resolved, total: items.length };
  }, [items]);

  const setStatus = async (iid, status) => {
    try {
      await api.patch(`/critical-issues/${iid}/status`, { status });
      toast.success("Status diubah");
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="critical-issues-page">
      {!canView ? (
        <div className="p-8 text-center text-slate-500 border border-slate-200 bg-slate-50" data-testid="critical-no-access">
          <div className="text-lg font-semibold text-[#0B1D3A] mb-1">Akses Ditolak</div>
          <div className="text-sm">Anda tidak memiliki akses Critical Issues. Modul ini dinonaktifkan oleh Access Control.</div>
        </div>
      ) : (<>
      <div className="flex items-start justify-between">
        <div>
          <p className="overline text-slate-500">Kontrol & Audit</p>
          <h1 className="font-display text-3xl text-[#0B1D3A]">Isu Kritis</h1>
          <p className="text-xs text-slate-500 mt-1">Persoalan manajemen & Log Keputusan. Bukan pengganti Tugas.</p>
        </div>
        {canCreate && (
          <Button data-testid="btn-new-ci" onClick={() => setDrawer({ open: true, existing: null })}
            className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold">
            <Plus size={13} className="mr-1" /> Isu Baru
          </Button>
        )}
        <div className="flex gap-2">
          {canManageCategories && (
            <Button data-testid="btn-manage-categories" variant="outline" onClick={() => setCategoryDlg(true)}
              className="rounded-none uppercase text-xs tracking-wider font-semibold border-slate-400 text-slate-700 hover:bg-slate-100">
              <SettingsIcon size={13} className="mr-1" /> Kelola Kategori
            </Button>
          )}
          <Button data-testid="btn-export-ci" variant="outline" onClick={exportCsv} disabled={exporting}
            className="rounded-none uppercase text-xs tracking-wider font-semibold border-slate-400 text-slate-700 hover:bg-slate-100">
            <Download size={13} className="mr-1" /> {exporting ? "Menyiapkan…" : ACTION_LABEL.export}
          </Button>
        </div>
      </div>

      <StickyContextBar
        segments={["Kontrol & Audit", "Isu Kritis", `${filtered.length} isu`]}
        extra={<span className="text-slate-400 normal-case">{filters.status === "all" ? "Semua Status" : STATUS_OPTS.find((s) => s.v === filters.status)?.label}</span>}
      />

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Kpi label="Total" value={kpi.total} tone="slate" testid="kpi-total" />
        <Kpi label="Terlambat" value={kpi.overdue} tone="rose" testid="kpi-overdue" />
        <Kpi label="Tanpa PIC" value={kpi.noPic} tone="amber" testid="kpi-nopic" />
        <Kpi label="Menunggu Keputusan" value={kpi.awaitingDec} tone="sky" testid="kpi-awaiting-dec" />
        <Kpi label="Dari Integritas Data" value={kpi.fromIntegrity} tone="orange" testid="kpi-integrity" />
        <Kpi label={COMMON_STATUS_LABEL.resolved} value={kpi.resolved} tone="emerald" testid="kpi-resolved" />
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input data-testid="ci-search" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Cari CI / judul…"
            className="pl-8 pr-3 py-2 border border-slate-300 text-xs w-64 focus:outline-none focus:border-[#FF5A1F]" />
        </div>
        <FilterSelect testid="ci-filter-status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          placeholder="Status" options={STATUS_OPTS.map((s) => ({ v: s.v, label: s.label }))} />
        <FilterSelect testid="ci-filter-severity" value={filters.severity} onChange={(v) => setFilters((f) => ({ ...f, severity: v }))}
          placeholder="Tingkat Keparahan" options={SEV_OPTS.map((s) => ({ v: s.v, label: s.label }))} />
        <FilterSelect testid="ci-filter-source" value={filters.source} onChange={(v) => setFilters((f) => ({ ...f, source: v }))}
          placeholder="Sumber" options={[{ v: "manual", label: "Manual" }, { v: "data_integrity", label: "Integritas Data" }]} />
        <div className="ml-auto text-[10px] text-slate-500">{filtered.length} isu</div>
      </div>

      {/* Master-Detail */}
      <div className="grid gap-4" style={{ gridTemplateColumns: "minmax(0, 60fr) minmax(0, 40fr)" }}>
        <section className="bg-white border border-slate-200" data-testid="ci-list">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 ens-sticky-thead">
              <tr className="text-left">
                <th className="px-3 py-2">Kode / Judul</th>
                <th className="px-3 py-2">Tingkat Keparahan</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">PIC</th>
                <th className="px-3 py-2">Target</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="p-6 text-center text-slate-400 italic">Memuat…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-slate-400 italic" data-testid="ci-empty">Belum ada critical issue.</td></tr>
              )}
              {filtered.map((i) => {
                const st = STATUS_OPTS.find((s) => s.v === i.status);
                const sv = SEV_OPTS.find((s) => s.v === i.severity);
                const isActive = i.id === selectedId;
                return (
                  <tr key={i.id} onClick={() => setSelectedId(i.id)} data-testid={`ci-row-${i.id}`}
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${isActive ? "bg-orange-50 border-l-4 border-l-[#FF5A1F]" : "border-l-4 border-l-transparent"}`}>
                    <td className="px-3 py-2">
                      <div className="font-mono text-[#0B1D3A] font-semibold">{i.code}</div>
                      <div className="text-slate-700 truncate max-w-[260px]" title={i.title}>{i.title}</div>
                      {i.source === "data_integrity" && <span className="text-[9px] uppercase tracking-wider text-orange-600 font-semibold">◆ Integritas Data</span>}
                    </td>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${sv?.cls}`}>{sv?.label}</span></td>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${st?.cls}`}>{st?.label}</span></td>
                    <td className="px-3 py-2 text-slate-700">{i.pic_name || <span className="italic text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-700">{i.target_resolve_date ? fmtDate(i.target_resolve_date) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <aside className="bg-white border border-slate-200 ens-sticky-aside" data-testid="ci-detail">
          {!loading && filtered.length === 0 && (
            <div className="p-8 text-center text-xs text-slate-400 italic">Pilih issue untuk lihat detail.</div>
          )}
          {!loading && selected && (
            <IssueDetail issue={selected} projects={projects} canUpdate={canUpdate} canResolveClose={canResolveClose} canRecordDecision={canRecordDecision} onStatus={setStatus}
              onAddDecision={() => setDecisionDlg({ open: true, issue: selected })}
              onEdit={() => setDrawer({ open: true, existing: selected })}
              onReload={load} />
          )}
        </aside>
      </div>

      <IssueDrawer
        open={drawer.open}
        onOpenChange={(v) => setDrawer({ open: v, existing: v ? drawer.existing : null })}
        existing={drawer.existing}
        categories={categories}
        onSaved={() => load()}
      />
      <AddDecisionDialog dlg={decisionDlg} onOpenChange={(v) => setDecisionDlg({ open: v, issue: v ? decisionDlg.issue : null })} onDone={load} />
      <ManageCategoriesDialog open={categoryDlg} onOpenChange={setCategoryDlg} categories={categories} onChanged={load} />
      </>)}
    </div>
  );
}

function Kpi({ label, value, tone = "slate", testid }) {
  const tones = { slate: "bg-slate-50 border-slate-200 text-slate-700", rose: "bg-rose-50 border-rose-200 text-rose-800", amber: "bg-amber-50 border-amber-200 text-amber-800", sky: "bg-sky-50 border-sky-200 text-sky-800", orange: "bg-orange-50 border-orange-200 text-orange-800", emerald: "bg-emerald-50 border-emerald-200 text-emerald-800" };
  return <div className={`border p-3 ${tones[tone]}`} data-testid={testid}><div className="overline text-[10px]">{label}</div><div className="font-display text-xl tabular-nums mt-0.5">{value}</div></div>;
}

function FilterSelect({ testid, value, onChange, placeholder, options }) {
  // 149-cd: <option> hanya boleh berisi plain text. Label default = "Semua {placeholder}"
  // untuk konsistensi bahasa (mis. "Semua Status", "Semua Severity").
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="h-9 border border-slate-300 px-2 text-xs bg-white focus:outline-none focus:border-[#FF5A1F]">
      <option value="all">{`Semua ${placeholder}`}</option>
      {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}

function IssueDetail({ issue, projects = [], canUpdate, canResolveClose, canRecordDecision, onStatus, onAddDecision, onEdit, onReload }) {
  const st = STATUS_OPTS.find((s) => s.v === issue.status);
  const sv = SEV_OPTS.find((s) => s.v === issue.severity);
  // 149-ce: resolve project_id → nama project untuk display; fallback ke id kalau tidak ada di list aktif.
  const projectName = issue.project_id
    ? (projects.find((p) => p.id === issue.project_id)?.project_name || projects.find((p) => p.id === issue.project_id)?.wo_number || projects.find((p) => p.id === issue.project_id)?.variation_order_name || issue.project_id.slice(0, 12) + "…")
    : null;
  const convertFollowUp = async (versionNo, idx, followUp) => {
    if (followUp.task_id) { toast.info(`Sudah ada task terkait (#${followUp.task_id.slice(0, 8)})`); return; }
    try {
      const { data } = await api.post(`/critical-issues/${issue.id}/decisions/${versionNo}/follow-ups/${idx}/to-task`);
      toast.success(`Task dibuat untuk ${data.assignee_name}. Cek My Work.`);
      onReload?.();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal convert follow-up ke task");
    }
  };
  return (
    <div className="p-4 space-y-3" data-testid={`ci-detail-${issue.id}`}>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${sv?.cls}`}>{sv?.label}</span>
          <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${st?.cls}`}>{st?.label}</span>
        </div>
        <div className="mt-2 font-mono text-lg text-[#0B1D3A]">{issue.code}</div>
        <div className="text-sm text-slate-800 font-semibold">{issue.title}</div>
      </div>
      {issue.description && (
        <div>
          <div className="overline text-slate-600">Deskripsi</div>
          <div className="text-xs text-slate-700 whitespace-pre-wrap">{issue.description}</div>
        </div>
      )}
      {issue.impacts && Object.values(issue.impacts).filter(Boolean).length > 0 && (
        <div>
          <div className="overline text-slate-600">Dampak</div>
          <div className="grid grid-cols-2 gap-1 text-[11px]">
            {["cost", "schedule", "client", "ops"].map((k) => issue.impacts?.[k] && (
              <div key={k} className="border border-slate-200 p-1.5">
                <div className="overline text-[9px] text-slate-500">{k}</div>
                <div className="text-slate-800">{issue.impacts[k]}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div><span className="overline text-slate-500">PIC</span><div className="text-slate-800">{issue.pic_name || "—"}</div></div>
        <div><span className="overline text-slate-500">Target</span><div className="text-slate-800 tabular-nums">{issue.target_resolve_date ? fmtDate(issue.target_resolve_date) : "—"}</div></div>
        <div><span className="overline text-slate-500">Kategori</span><div className="text-slate-800">{issue.category || "—"}</div></div>
        <div><span className="overline text-slate-500">Prioritas</span><div className="text-slate-800 capitalize">{issue.priority || "normal"}</div></div>
        <div data-testid="ci-detail-project"><span className="overline text-slate-500">Project</span><div className="text-slate-800 truncate" title={projectName || ""}>{projectName || "—"}</div></div>
        <div data-testid="ci-detail-division"><span className="overline text-slate-500">Divisi</span><div className="text-slate-800">{issue.division || "—"}</div></div>
        <div><span className="overline text-slate-500">Sumber</span><div className="text-slate-800 capitalize">{(issue.source || "manual").replace("_", " ")}</div></div>
        <div><span className="overline text-slate-500">Dibuat</span><div className="text-slate-800 text-[10px]">{issue.created_by_name} · {issue.created_at ? fmtDate(issue.created_at) : ""}</div></div>
      </div>
      {(issue.references || []).length > 0 && (
        <div>
          <div className="overline text-slate-600">Referensi ({issue.references.length})</div>
          <ul className="text-[11px] space-y-0.5">
            {issue.references.map((r) => (
              <li key={`${r.type}-${r.id}`} className="flex items-center gap-2">
                <span className="inline-block px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-slate-100 border border-slate-200">{r.type}</span>
                <span className="text-slate-700 truncate" title={r.label || r.id}>{r.label || r.id.slice(0, 16)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(issue.attachments || []).length > 0 && (
        <div>
          <div className="overline text-slate-600">Lampiran ({issue.attachments.length})</div>
          <ul className="text-[11px] space-y-0.5">
            {issue.attachments.map((a) => (
              <li key={a.storage_path || a.url} className="flex items-center gap-2">
                <a href={`${(api.defaults?.baseURL || "").replace(/\/api$/, "")}${a.url}`} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline truncate flex-1" title={a.name}>{a.name}</a>
                <span className="text-[9px] text-slate-500 tabular-nums whitespace-nowrap">{Math.round((a.size || 0) / 1024)} KB</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {issue.decisions?.length > 0 && (
        <div>
          <div className="overline text-slate-600">Log Keputusan ({issue.decisions.length} versi)</div>
          <div className="space-y-2">
            {issue.decisions.slice().reverse().map((d) => (
              <div key={d.version} className="border-l-2 border-[#FF5A1F] pl-2 text-[11px]">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-semibold text-[#0B1D3A]">v{d.version}</span>
                  <span className="text-[9px] text-slate-500 tabular-nums">{fmtDate(d.decided_at)}</span>
                </div>
                <div className="text-slate-800">{d.decision}</div>
                {d.rationale && <div className="text-[10px] text-slate-500 italic">{d.rationale}</div>}
                <div className="text-[9px] text-slate-400">— {d.decided_by_name}</div>
                {/* 149-bw: Follow-ups w/ Convert-to-Task button */}
                {(d.follow_ups || []).length > 0 && (
                  <div className="mt-1 space-y-1">
                    {d.follow_ups.map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-[10px] bg-slate-50 border border-slate-200 p-1.5" data-testid={`ci-followup-${d.version}-${i}`}>
                        <div className="flex-1">
                          <div className="text-slate-800">▸ {f.action}</div>
                          <div className="text-[9px] text-slate-500">
                            {f.owner_name && <>Owner: <b>{f.owner_name}</b> · </>}
                            {f.due_date && <>Due: <b>{fmtDate(f.due_date)}</b></>}
                          </div>
                          {f.task_id && (
                            <a href={`/tasks?open=${f.task_id}`} className="text-[9px] text-sky-700 hover:underline font-mono">→ Task #{f.task_id.slice(0, 8)}</a>
                          )}
                        </div>
                        {canUpdate && !f.task_id && (
                          <button type="button" onClick={() => convertFollowUp(d.version, i, f)}
                            data-testid={`ci-followup-to-task-${d.version}-${i}`}
                            className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold border border-sky-500 text-sky-700 hover:bg-sky-50 whitespace-nowrap">
                            → Task
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="pt-2 border-t border-slate-200 flex flex-wrap gap-1.5">
        {canUpdate && (
          <Button size="sm" variant="outline" onClick={onEdit} data-testid="ci-edit"
            className="rounded-none text-[10px] uppercase tracking-wider border-slate-400 text-slate-700 hover:bg-slate-100">
            <Pencil size={11} className="mr-1" /> Edit
          </Button>
        )}
        {canRecordDecision && (
          <Button size="sm" variant="outline" onClick={onAddDecision} data-testid="ci-add-decision"
            className="rounded-none text-[10px] uppercase tracking-wider">+ Decision</Button>
        )}
        {canResolveClose && issue.status !== "resolved" && issue.status !== "closed" && (
          <Button size="sm" onClick={() => onStatus(issue.id, "resolved")} data-testid="ci-resolve"
            className="rounded-none text-[10px] uppercase tracking-wider bg-emerald-700 hover:bg-emerald-800">Tandai Terselesaikan</Button>
        )}
        {canResolveClose && issue.status === "resolved" && (
          <Button size="sm" onClick={() => onStatus(issue.id, "closed")} data-testid="ci-close"
            className="rounded-none text-[10px] uppercase tracking-wider bg-slate-700 hover:bg-slate-800">Close Issue</Button>
        )}
        {!canRecordDecision && !canResolveClose && (
          <div className="text-[10px] italic text-slate-400">View-only untuk role Anda.</div>
        )}
      </div>
    </div>
  );
}

function ManageCategoriesDialog({ open, onOpenChange, categories, onChanged }) {
  // 149-cq (PROMPT 16-R2A): Kelola Kategori — CRUD kategori (butuh
  // critical_issue_categories.update). Archive/unarchive (no hard-delete history).
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  useEffect(() => { if (open) { setRows(categories); setNewName(""); } }, [open, categories]);
  const reload = async () => {
    try {
      const { data } = await api.get("/critical-issues/categories/all");
      setRows(data || []);
      onChanged?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal muat kategori"); }
  };
  const add = async () => {
    const n = newName.trim();
    if (!n) return;
    setBusy(true);
    try {
      await api.post("/critical-issues/categories", { name: n });
      toast.success("Kategori dibuat");
      setNewName("");
      await reload();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal buat kategori"); }
    finally { setBusy(false); }
  };
  const archive = async (cid, active) => {
    setBusy(true);
    try {
      await api.post(`/critical-issues/categories/${cid}/${active ? "archive" : "unarchive"}`);
      toast.success(active ? "Kategori diarsipkan" : "Kategori diaktifkan");
      await reload();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal ubah status kategori"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="ci-manage-categories-dlg">
        <DialogHeader>
          <DialogTitle className="font-display">Kelola Kategori</DialogTitle>
          <DialogDescription className="text-xs">Aktifkan atau arsipkan kategori. Kategori yang diarsipkan tidak muncul di form Issue baru, tetapi histori issue lama tetap.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Input data-testid="cat-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nama kategori baru…" className="rounded-none" />
            <Button data-testid="cat-add" disabled={busy || !newName.trim()} onClick={add} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] whitespace-nowrap"><Plus size={12} className="mr-1" /> Tambah</Button>
          </div>
          <div className="border border-slate-200 divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {rows.length === 0 && <div className="p-4 text-center text-xs italic text-slate-400">Belum ada kategori.</div>}
            {rows.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`cat-row-${c.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${c.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                  <span className={c.is_active ? "text-[#0B1D3A] font-semibold" : "text-slate-400 line-through"}>{c.name}</span>
                </div>
                <button type="button" disabled={busy} onClick={() => archive(c.id, c.is_active)}
                  data-testid={`cat-toggle-${c.id}`}
                  className={`text-[10px] uppercase tracking-wider font-semibold ${c.is_active ? "text-rose-600 hover:text-rose-800" : "text-emerald-600 hover:text-emerald-800"}`}>
                  {c.is_active ? "Arsipkan" : "Aktifkan"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreateIssueDialog({ open, onOpenChange, categories, onDone }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", category: "", description: "", severity: "medium", priority: "normal", target_resolve_date: "", pic_name: "" });
  useEffect(() => { if (open) setForm({ title: "", category: "", description: "", severity: "medium", priority: "normal", target_resolve_date: "", pic_name: "" }); }, [open]);
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    setBusy(true);
    try {
      await api.post("/critical-issues", { ...form, source: "manual" });
      toast.success("Critical Issue dibuat");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="ci-create-dlg">
        <DialogHeader><DialogTitle className="font-display">Isu Baru</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="overline text-slate-600">Judul *</Label><Input data-testid="ci-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="overline text-slate-600">Tingkat Keparahan</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger className="rounded-none h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">{SEV_OPTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="overline text-slate-600">Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-none h-9"><SelectValue placeholder="Pilih…" /></SelectTrigger>
                <SelectContent className="rounded-none">{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="overline text-slate-600">Deskripsi</Label><textarea data-testid="ci-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="overline text-slate-600">PIC</Label><Input value={form.pic_name} onChange={(e) => setForm({ ...form, pic_name: e.target.value })} className="rounded-none" /></div>
            <div><Label className="overline text-slate-600">Target Selesai</Label><Input type="date" value={form.target_resolve_date} onChange={(e) => setForm({ ...form, target_resolve_date: e.target.value })} className="rounded-none" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button data-testid="ci-submit" onClick={submit} disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">{busy ? "Menyimpan…" : "Simpan Issue"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDecisionDialog({ dlg, onOpenChange, onDone }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ decision: "", rationale: "", options_considered: "", revision_reason: "", follow_ups: [] });
  useEffect(() => { if (dlg.open) setForm({ decision: "", rationale: "", options_considered: "", revision_reason: "", follow_ups: [] }); }, [dlg.open]);
  const addFollowUp = () => setForm((f) => ({ ...f, follow_ups: [...f.follow_ups, { action: "", owner_name: "", due_date: "" }] }));
  const updateFollowUp = (i, patch) => setForm((f) => ({ ...f, follow_ups: f.follow_ups.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const removeFollowUp = (i) => setForm((f) => ({ ...f, follow_ups: f.follow_ups.filter((_, idx) => idx !== i) }));
  const submit = async () => {
    if (!form.decision.trim()) { toast.error("Keputusan wajib diisi"); return; }
    setBusy(true);
    try {
      const payload = { ...form, follow_ups: form.follow_ups.filter((f) => f.action.trim()) };
      if (!payload.revision_reason) delete payload.revision_reason;
      await api.post(`/critical-issues/${dlg.issue.id}/decisions`, payload);
      toast.success(`Keputusan tercatat${payload.follow_ups.length > 0 ? ` (${payload.follow_ups.length} follow-up)` : ""}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal"); }
    finally { setBusy(false); }
  };
  if (!dlg.issue) return null;
  return (
    <Dialog open={dlg.open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="ci-decision-dlg">
        <DialogHeader>
          <DialogTitle className="font-display">Catat Keputusan</DialogTitle>
          <div className="text-xs text-slate-500">Issue: <b>{dlg.issue.code}</b> — {dlg.issue.title}</div>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="overline text-slate-600">Keputusan *</Label><textarea data-testid="dec-decision" value={form.decision} onChange={(e) => setForm({ ...form, decision: e.target.value })} rows={2} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]" /></div>
          <div><Label className="overline text-slate-600">Alasan</Label><textarea data-testid="dec-rationale" value={form.rationale} onChange={(e) => setForm({ ...form, rationale: e.target.value })} rows={2} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]" /></div>
          <div><Label className="overline text-slate-600">Opsi Dipertimbangkan</Label><textarea data-testid="dec-options" value={form.options_considered} onChange={(e) => setForm({ ...form, options_considered: e.target.value })} rows={2} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]" /></div>
          {/* 149-bw: Follow-up action items — user bisa convert ke Task setelah save */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="overline text-slate-600">Follow-up Actions</Label>
              <button type="button" onClick={addFollowUp} data-testid="dec-add-followup"
                className="text-[10px] uppercase tracking-wider font-semibold text-sky-700 hover:text-sky-900">+ Tambah</button>
            </div>
            {form.follow_ups.length === 0 && <div className="text-[10px] text-slate-400 italic">Belum ada action. Klik "+ Tambah" untuk daftar tindak lanjut. Bisa dikonversi ke Task setelah keputusan disimpan.</div>}
            <div className="space-y-1.5">
              {form.follow_ups.map((f, i) => (
                <div key={i} className="border border-slate-200 p-2 space-y-1" data-testid={`dec-followup-form-${i}`}>
                  <div className="flex items-start gap-2">
                    <input value={f.action} onChange={(e) => updateFollowUp(i, { action: e.target.value })}
                      placeholder="Action item…"
                      className="flex-1 px-2 py-1 border border-slate-300 text-xs focus:outline-none focus:border-[#FF5A1F]" />
                    <button type="button" onClick={() => removeFollowUp(i)} className="text-rose-600 hover:text-rose-800 px-1">×</button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <input value={f.owner_name} onChange={(e) => updateFollowUp(i, { owner_name: e.target.value })}
                      placeholder="Owner (nama)…" className="px-2 py-1 border border-slate-300 text-[11px] focus:outline-none focus:border-[#FF5A1F]" />
                    <input type="date" value={f.due_date} onChange={(e) => updateFollowUp(i, { due_date: e.target.value })}
                      className="px-2 py-1 border border-slate-300 text-[11px] focus:outline-none focus:border-[#FF5A1F]" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {(dlg.issue.decisions?.length || 0) > 0 && (
            <div><Label className="overline text-slate-600">Alasan Revisi (opsional)</Label><Input value={form.revision_reason} onChange={(e) => setForm({ ...form, revision_reason: e.target.value })} className="rounded-none" placeholder="Kenapa revisi dari v" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button data-testid="dec-submit" onClick={submit} disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">{busy ? "Menyimpan…" : "Catat Keputusan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
